import React from 'react';
import Table from '../../table/Table';
import MappingsModal from './MappingsModal';
import { ApiAttributeMapping, ApiAttributeMappingFieldEnum } from '../../api';
import SalvPage from '../../common/SalvPage';
import { getConfigurationUrl } from '../../navigation/helper';
import { useQueryAttributeMappings, useUpdateMappings } from './mappingsActions';
import DataLoadingErrorFallback from '../../common/errors/DataLoadingErrorFallback';
import { SalvProgressCircle } from '@salvapp/ui-components';
import { useQueryEntitySchema } from '../entitySchemaActions';
import { InternalApiEntityType } from '../../api-internal';
import { ConfigurationProduct } from '../../settings/configuration/Configuration';

export const AttributeMappings = () => {
  const {
    data: attributeMappings,
    refetch,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
    error: attributeMappingError,
  } = useQueryAttributeMappings();
  const { mutateAsync: updateMappings } = useUpdateMappings();
  const {
    data: personSchema,
    isPending: isPersonSchemaPending,
    isError: isPersonSchemaError,
    error: personError,
  } = useQueryEntitySchema(InternalApiEntityType.PERSON);
  const {
    data: transactionSchema,
    isPending: isTransactionSchemaPending,
    isError: isTransactionSchemaError,
    error: transactionError,
  } = useQueryEntitySchema(InternalApiEntityType.TRANSACTION);

  if (isAttributeMappingsError || isPersonSchemaError || isTransactionSchemaError) {
    return (
      <DataLoadingErrorFallback
        message={"Could not load Attribute Mappings' resources"}
        error={[attributeMappingError, personError, transactionError]}
      />
    );
  } else if (isAttributeMappingsPending || isPersonSchemaPending || isTransactionSchemaPending) {
    return <SalvProgressCircle ariaLabel={"Loading Attribute Mappings' resources"} />;
  }

  const handleChange = async (mapping: ApiAttributeMapping) => {
    await updateMappings(mapping);
  };

  const fetchPage = async () => {
    await refetch();
  };

  const mappingsMap = new Map(Object.entries(attributeMappings.map));

  for (const mappingField of Object.values(ApiAttributeMappingFieldEnum)) {
    if (!mappingsMap.has(mappingField)) {
      mappingsMap.set(mappingField, { field: mappingField, mapping: '' });
    }
  }

  return (
    <SalvPage
      title={'Attribute Mappings'}
      breadcrumb={{
        title: 'Configuration',
        to: getConfigurationUrl(ConfigurationProduct.GENERAL),
      }}
    >
      <Table
        hover={false}
        total={mappingsMap.size}
        page={0}
        size={mappingsMap.size}
        items={Array.from(mappingsMap.values())}
        fetchPage={fetchPage}
        header={() => (
          <tr>
            <th>Field</th>
            <th>Mapping</th>
            <th>Boolean true</th>
            <th>Format</th>
            <th className='text-end'>Actions</th>
          </tr>
        )}
        body={(mappings) =>
          mappings.map((mapping) => (
            <tr key={mapping.id}>
              <td>{mapping.field}</td>
              <td>{mapping.mapping}</td>
              <td>{mapping.booleanTrueValue}</td>
              <td>{mapping.format}</td>
              <td className='text-end'>
                <MappingsModal
                  mapping={mapping}
                  onChange={handleChange}
                  schemaFields={[...personSchema.schemaFields, ...transactionSchema.schemaFields]}
                />
              </td>
            </tr>
          ))
        }
      />
    </SalvPage>
  );
};

export default AttributeMappings;
